import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { marketplaceApi } from '../../api/marketplace';
import CardImageLightbox from './CardImageLightbox';
import { useLanguage } from '../../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../../context/ConfirmD10SContext';
import {
  isPeriodMarketplaceLoan,
  getLoanAcceptPrice,
  getLoanCardTitle,
  getLoanPeriodHours,
  getLoanPeriodsRemaining,
  getLoanPeriodsTotal,
  getLoanPeriodsUsedDisplay
} from '../../utils/loanDisplayUtils';

const LoanPricingDetails = ({ loan, t }) => {
  const isPeriod = isPeriodMarketplaceLoan(loan);
  const periodHours = getLoanPeriodHours(loan);
  const pricePerPeriod = parseFloat(loan.price_per_period) || parseFloat(loan.total_price) || 0;
  const acceptPrice = getLoanAcceptPrice(loan);
  const periodsTotal = getLoanPeriodsTotal(loan);
  const periodsRemaining = getLoanPeriodsRemaining(loan);
  const periodsUsedDisplay = getLoanPeriodsUsedDisplay(loan);
  const pricePerGW = parseFloat(loan.price_per_gw) || 0;

  if (isPeriod) {
    return (
      <>
        <p className="text-xs text-[#64748b] mb-2">{t('marketplaceCards.periodBorrowOneAtATime')}</p>
        <div className="flex items-center justify-between text-sm bg-blue-500/10 px-2 py-1.5 rounded">
          <span className="text-[#1e293b] font-semibold">{t('marketplaceCards.youRentOnePeriod')}:</span>
          <span className="text-[#4169E1] font-bold">
            {t('marketplaceCards.periodHours', { hours: periodHours })}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.pricePerPeriod')}:</span>
          <span className="text-[#4169E1] font-semibold">{pricePerPeriod.toFixed(2)} DIOS</span>
        </div>
        {(periodsRemaining > 0 || periodsUsedDisplay > 0) && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">{t('marketplaceCards.listingSlotsRemaining')}:</span>
            <span className="text-[#1e293b] font-semibold">
              {periodsRemaining} / {periodsTotal}
              {loan.status === 'active'
                ? ` (${t('marketplaceCards.periodInUseNow')})`
                : periodsUsedDisplay > 0
                  ? ` (${t('marketplaceCards.periodsUsedCount', { count: periodsUsedDisplay })})`
                  : ''}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
          <span className="text-[#64748b] text-sm font-medium">{t('marketplaceCards.costPerPeriod')}:</span>
          <span className="text-[#4169E1] font-bold text-lg">{acceptPrice.toFixed(2)} DIOS</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#64748b] font-medium">{t('marketplaceCards.pricePerGameweek')}:</span>
        <span className="text-[#4169E1] font-semibold">{pricePerGW.toFixed(2)} DIOS</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#64748b] font-medium">{t('marketplaceCards.durationLabel')}:</span>
        <span className="text-[#1e293b] font-semibold">{t('marketplaceCards.gameweeks', { count: loan.duration_gw })}</span>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
        <span className="text-[#64748b] text-sm font-medium">{t('marketplaceCards.totalCost')}:</span>
        <span className="text-[#4169E1] font-bold text-lg">{acceptPrice.toFixed(2)} DIOS</span>
      </div>
    </>
  );
};

const LoanCard = ({ loan, user, onAcceptSuccess, onError }) => {
  const { t } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isCopiesOpen, setIsCopiesOpen] = useState(false);
  const [copies, setCopies] = useState([]);
  const [copiesLoading, setCopiesLoading] = useState(false);
  const [copiesError, setCopiesError] = useState('');
  const [acceptingCopyIds, setAcceptingCopyIds] = useState({});
  const isPeriodLoan = isPeriodMarketplaceLoan(loan);
  const isPeriodInUse = isPeriodLoan && loan.status === 'active';
  const availableCount = parseInt(loan.available_count, 10) || 1;
  const hasMultipleCopies = availableCount > 1;
  const isOwnLoan = loan.lender_id === (user?.linkedUserId ?? user?.id);
  const cardTitle = getLoanCardTitle(loan);
  const lenderUsername =
    loan.lender?.username || loan.lender_username || null;
  const lenderDisplay = hasMultipleCopies
    ? t('marketplaceCards.multipleLenders')
    : lenderUsername || (loan.lender_id ? `#${loan.lender_id}` : t('common.unknown'));

  useEffect(() => {
    setIsCopiesOpen(false);
    setCopies([]);
    setCopiesError('');
    setAcceptingCopyIds({});
  }, [loan.id]);

  const loadCopies = async () => {
    if (!loan.template_id) return;
    setCopiesLoading(true);
    setCopiesError('');
    try {
      const response = await marketplaceApi.getLoansByTemplate(loan.template_id);
      if (response?.success) {
        setCopies(response.loans || []);
      } else {
        setCopies([]);
        setCopiesError(response?.error || t('marketplaceCards.copiesLoadFailed'));
      }
    } catch (error) {
      setCopies([]);
      setCopiesError(error.message || t('marketplaceCards.copiesLoadFailed'));
    } finally {
      setCopiesLoading(false);
    }
  };

  const handleOpenCopies = () => {
    setIsCopiesOpen(true);
    if (copies.length === 0 && !copiesLoading) {
      loadCopies();
    }
  };

  const acceptLoan = async (loanToAccept, setBusy) => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToAcceptLoan'));
      return;
    }

    if (loanToAccept.lender_id === (user?.linkedUserId ?? user.id)) {
      onError(t('marketplaceCards.acceptOwnLoan'));
      return;
    }

    const price = getLoanAcceptPrice(loanToAccept);
    const confirmAction = isPeriodMarketplaceLoan(loanToAccept)
      ? t('marketplaceCards.acceptOnePeriod')
      : t('marketplaceCards.acceptLoan');
    const confirmHint = isPeriodMarketplaceLoan(loanToAccept)
      ? t('marketplaceCards.acceptOnePeriodHint', {
          hours: getLoanPeriodHours(loanToAccept),
          amount: price.toFixed(2)
        })
      : undefined;
    const ok = await confirm(price, confirmAction, confirmHint ? { firstMessage: confirmHint } : undefined);
    if (!ok) return;

    setBusy(true);
    try {
      await marketplaceApi.acceptLoan(loanToAccept.id, {
        borrowerId: user?.linkedUserId ?? user.id
      });
      onAcceptSuccess();
      setIsCopiesOpen(false);
    } catch (error) {
      onError(error.message || t('marketplaceCards.acceptLoanFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleAcceptLoan = async () => {
    if (hasMultipleCopies) {
      handleOpenCopies();
      return;
    }

    await acceptLoan(loan, setIsAccepting);
  };

  const handleAcceptCopy = async (copy) => {
    setAcceptingCopyIds((prev) => ({ ...prev, [copy.id]: true }));
    await acceptLoan(copy, (busy) => {
      setAcceptingCopyIds((prev) => ({ ...prev, [copy.id]: busy }));
    });
  };

  return (
    <div className="football-card glass-football p-6 card-hover relative">
      {hasMultipleCopies && (
        <button
          type="button"
          onClick={handleOpenCopies}
          className="absolute top-2 right-2 z-10 bg-yellow-600 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg border-2 border-white/50 hover:bg-yellow-700 transition-colors"
          aria-label={t('marketplaceCards.viewCopiesAria', { count: availableCount })}
        >
          x{availableCount}
        </button>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="marketplace-badge marketplace-badge-loan px-3 py-1 text-sm font-semibold">
          🔄 {isPeriodLoan ? t('marketplaceCards.periodLoanBadge') : t('marketplaceCards.loanBadge')}
        </span>
        {isPeriodInUse && (
          <span className="marketplace-period-in-use-badge marketplace-badge text-xs px-3 py-1">
            {t('marketplaceCards.periodInUseNow')}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-3 mb-3">
        <CardImageLightbox
          card={loan}
          alt={cardTitle}
          thumbnailClassName="w-12 h-12 rounded-full object-cover border border-white/60 shadow-sm"
        />
        <h3 className="text-xl font-bold text-[#1e293b]">{cardTitle}</h3>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.positionLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">{loan.player?.position || t('common.notAvailable')}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.rarityLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">
            ⭐ {loan.rarity?.display_name || loan.rarity?.name || t('common.notAvailable')}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">Card ID:</span>
          <span className="text-[#94a3b8] font-mono text-xs">#{loan.sample_card_id ?? loan.card_id}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.loanFromLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">{lenderDisplay}</span>
        </div>
        <div className="border-t border-[#E2E8F0] pt-2 mt-2 space-y-2">
          <LoanPricingDetails loan={loan} t={t} />
        </div>
      </div>
      
      <button 
        className="btn-football w-full"
        onClick={handleAcceptLoan}
        disabled={
          isAccepting
          || !user?.id
          || isPeriodInUse
          || (!hasMultipleCopies && isOwnLoan)
        }
      >
        {isAccepting
          ? t('marketplaceCards.processing')
          : isPeriodInUse
            ? t('marketplaceCards.periodInUseNow')
            : hasMultipleCopies
              ? t('marketplaceCards.viewCopiesLoan')
              : isPeriodLoan
                ? `✅ ${t('marketplaceCards.acceptOnePeriod')}`
                : `✅ ${t('marketplaceCards.acceptLoan')}`}
      </button>

      {isCopiesOpen && ReactDOM.createPortal(
        <div
          className="marketplace-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ width: '100vw', height: '100vh' }}
          onClick={() => setIsCopiesOpen(false)}
        >
          <div
            className="football-card glass-football marketplace-modal-card w-full max-w-4xl p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold uppercase tracking-wider">
                {t('marketplaceCards.availableCopies', { count: availableCount })}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsCopiesOpen(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            {copiesLoading && (
              <div className="text-center py-8 text-white/70">{t('marketplaceCards.loadingCopies')}</div>
            )}

            {!copiesLoading && copiesError && (
              <div className="text-center py-6 text-red-300">{copiesError}</div>
            )}

            {!copiesLoading && !copiesError && copies.length === 0 && (
              <div className="text-center py-6 text-white/70">{t('marketplaceCards.noCopies')}</div>
            )}

            {!copiesLoading && copies.length > 0 && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {copies.map((copy) => {
                  const copyAcceptPrice = getLoanAcceptPrice(copy);
                  const copyCardId = copy.sample_card_id ?? copy.card_id ?? copy.card?.id;
                  const isAcceptingCopy = acceptingCopyIds[copy.id];
                  const isOwnCopy = copy.lender_id === (user?.linkedUserId ?? user?.id);
                  const copyIsPeriod = isPeriodMarketplaceLoan(copy);
                  return (
                    <div
                      key={copy.id}
                      className="border border-white/10 p-4 bg-black/40"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-white">
                          {t('marketplaceCards.loanNumber', { id: copy.id })}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm mb-3">
                        <div>
                          <div className="text-white/60 text-xs">Card ID</div>
                          <div className="text-white font-mono text-xs">#{copyCardId ?? t('common.notAvailable')}</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">
                            {copyIsPeriod ? t('marketplaceCards.pricePerPeriod') : t('marketplaceCards.pricePerGameweek')}
                          </div>
                          <div className="text-[#00D9FF] font-bold">
                            {(copyIsPeriod
                              ? parseFloat(copy.price_per_period) || copyAcceptPrice
                              : parseFloat(copy.price_per_gw) || 0
                            ).toFixed(2)} DIOS
                          </div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">
                            {copyIsPeriod ? t('marketplaceCards.costPerPeriod') : t('marketplaceCards.totalCost')}
                          </div>
                          <div className="text-[#00D9FF] font-bold">{copyAcceptPrice.toFixed(2)} DIOS</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">{t('marketplaceCards.loanFromLabel')}</div>
                          <div className="text-white font-semibold">
                            {copy.lender?.username || copy.lender_username || (copy.lender_id ? `#${copy.lender_id}` : t('common.unknown'))}
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn-football w-full"
                            onClick={() => handleAcceptCopy(copy)}
                            disabled={isAcceptingCopy || !user?.id || isOwnCopy}
                          >
                            {isAcceptingCopy
                              ? t('marketplaceCards.processing')
                              : copyIsPeriod
                                ? `✅ ${t('marketplaceCards.acceptOnePeriod')}`
                                : `✅ ${t('marketplaceCards.acceptLoan')}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LoanCard;
